/**
 * Layer hierarchy — two alternating surface tones for nested cards.
 *
 * Stack model (depth from the page backdrop):
 *
 *   layer 0  ── page backdrop (`background-light` / `background-dark`)
 *   layer 1  ── top-level card on the page (hero card, table card, kanban column)
 *   layer 2  ── card sitting inside a layer-1 container (stat card, modal section)
 *   layer 3  ── inner element inside a layer-2 card (AMS box, sub-card)
 *
 * Only TWO tones are used and they alternate by depth — odd levels share Tone A,
 * even levels share Tone B. Each layer sits VISIBLY LIGHTER than the one wrapping
 * it (page → layer-1 lighter → layer-2 lighter still → layer-3 back to layer-1
 * tone). Layer-2 reuses the same translucent strip as table toolbars/list
 * headers, so cards anidadas read as "elevated" the same way header strips do.
 *
 *                    Light                Dark
 *   Tone A (opaque)  `bg-white`           `surface-dark`        (#251c14)
 *   Tone B (lighter) `bg-slate-50/50`     `slate-800/50`         (translucent)
 *
 *   layer 1  → Tone A  (opaque card on the page)
 *   layer 2  → Tone B  (translucent strip — lighter than layer-1)
 *   layer 3  → Tone A  (opaque again — alternates back)
 *
 * Pick the layer by counting depth from the page, NOT by aesthetic preference.
 * If two siblings live inside the same parent they share the same layer.
 *
 * Floating layer (modals, popovers, inputs, sidebar) keeps its own tokens
 * (`bg-white dark:bg-surface-dark`) — see Standards §"Surface and background
 * tokens". Same colour as layer-1 by design; they're separated by shadow +
 * position-fixed.
 *
 * The bundles below pair the bg with the matching border, rounding and shadow
 * (rounding scale matches `documentation/Standards.md` § "Tarjetas — escala de
 * rounding"):
 *
 *   layer 1 → rounded-2xl (primary container)
 *   layer 2 → rounded-xl  (secondary section)
 *   layer 3 → rounded-lg  (compact inner)
 *
 * Use the `bg*` variants when you need the colour alone (e.g. a row inside a
 * table that should not have its own border / rounding).
 */

/** Background only — layer 1 (Tone A: opaque card surface). */
export const bgLayer1 = 'bg-white dark:bg-surface-dark';
/** Background only — layer 2 (Tone B: translucent strip, lighter than layer 1).
 *  Same surface used by `tableToolbarStripClass` for header rows. */
export const bgLayer2 = 'bg-slate-50/50 dark:bg-slate-800/50';
/** Background only — layer 3 (Tone A again, alternates back to opaque). */
export const bgLayer3 = bgLayer1;

/** Full card bundle — layer 1: top-level card on the page. */
export const cardLayer1 =
    `rounded-2xl border border-border-subtle dark:border-border-subtle-dark ${bgLayer1} shadow-sm`;
/** Full card bundle — layer 2: card nested inside a layer-1 container. */
export const cardLayer2 =
    `rounded-xl border border-border-subtle dark:border-border-subtle-dark ${bgLayer2} shadow-sm`;
/** Full card bundle — layer 3: inner element nested inside a layer-2 card. */
export const cardLayer3 =
    `rounded-lg border border-border-subtle dark:border-border-subtle-dark ${bgLayer3} shadow-sm`;
